import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Send, Save } from 'lucide-react';
import api, { extrairMensagemErro } from '../services/api';
import Paginacao, { MetadadosPaginacao, paginacaoVazia } from '../components/Paginacao';
import { obterUsuarioSessao } from '../permissions';

type TipoFeedback = 'BUG' | 'SUGESTAO' | 'DESEMPENHO' | 'USABILIDADE' | 'OUTRO';
type StatusFeedback = 'NOVO' | 'EM_ANALISE' | 'RESOLVIDO' | 'ARQUIVADO';

type Feedback = {
  id: string;
  tenant_id: string;
  usuario_id: string;
  tipo: TipoFeedback;
  titulo: string;
  mensagem: string;
  nota?: number | null;
  status: StatusFeedback;
  resposta_admin?: string | null;
  criado_em: string;
  atualizado_em: string;
  usuario?: { nome?: string; perfil?: string };
  tenant?: { nome_empresa?: string };
};

const tipos: TipoFeedback[] = ['BUG', 'SUGESTAO', 'DESEMPENHO', 'USABILIDADE', 'OUTRO'];
const statuses: StatusFeedback[] = ['NOVO', 'EM_ANALISE', 'RESOLVIDO', 'ARQUIVADO'];

const statusClasse = (status: StatusFeedback) => {
  if (status === 'RESOLVIDO') return 'status-done';
  if (status === 'ARQUIVADO') return 'muted';
  if (status === 'EM_ANALISE') return 'status-warning';
  return '';
};

const dataHora = (valor?: string) =>
  valor ? new Date(valor).toLocaleString('pt-PT') : '-';

const FeedbackTabela: React.FC<{
  feedbacks: Feedback[];
  vazio: string;
  admin?: boolean;
  drafts?: Record<string, { status: StatusFeedback; resposta_admin: string }>;
  salvandoId?: string | null;
  onDraftChange?: (id: string, campo: 'status' | 'resposta_admin', valor: string) => void;
  onSalvar?: (feedback: Feedback) => void;
}> = ({ feedbacks, vazio, admin = false, drafts = {}, salvandoId, onDraftChange, onSalvar }) => (
  <div className="table-shell feedback-table">
    <table>
      <thead>
        <tr>
          <th>Título</th>
          <th>Tipo</th>
          <th>Status</th>
          <th>Nota</th>
          <th>Utilizador</th>
          <th>Tenant</th>
          <th>Mensagem</th>
          <th>Resposta</th>
          {admin && <th>Ação</th>}
        </tr>
      </thead>
      <tbody>
        {feedbacks.map((feedback) => {
          const draft = drafts[feedback.id] || {
            status: feedback.status,
            resposta_admin: feedback.resposta_admin || ''
          };

          return (
            <tr key={feedback.id}>
              <td>
                {feedback.titulo}
                <small>{dataHora(feedback.criado_em)}</small>
              </td>
              <td>{feedback.tipo}</td>
              <td>
                {admin ? (
                  <select
                    value={draft.status}
                    onChange={(evento) => onDraftChange?.(feedback.id, 'status', evento.target.value)}
                  >
                    {statuses.map((status) => <option key={status} value={status}>{status}</option>)}
                  </select>
                ) : (
                  <span className={`status-pill ${statusClasse(feedback.status)}`}>{feedback.status}</span>
                )}
              </td>
              <td>{feedback.nota || '-'}</td>
              <td>{feedback.usuario?.nome || feedback.usuario_id}<small>{feedback.usuario?.perfil || ''}</small></td>
              <td>{feedback.tenant?.nome_empresa || feedback.tenant_id}</td>
              <td>{feedback.mensagem}</td>
              <td>
                {admin ? (
                  <textarea
                    value={draft.resposta_admin}
                    maxLength={4000}
                    onChange={(evento) => onDraftChange?.(feedback.id, 'resposta_admin', evento.target.value)}
                    placeholder="Resposta administrativa"
                  />
                ) : (
                  feedback.resposta_admin || '-'
                )}
              </td>
              {admin && (
                <td>
                  <button
                    className="botao botao-secundario botao-compacto"
                    type="button"
                    onClick={() => onSalvar?.(feedback)}
                    disabled={salvandoId === feedback.id}
                    title="Guardar resposta"
                  >
                    <Save size={16} />
                    Guardar
                  </button>
                </td>
              )}
            </tr>
          );
        })}
        {feedbacks.length === 0 && <tr><td colSpan={admin ? 9 : 8}>{vazio}</td></tr>}
      </tbody>
    </table>
  </div>
);

const FeedbackPlataforma: React.FC = () => {
  const usuario = obterUsuarioSessao();
  const perfil = usuario?.perfil;
  const superAdmin = perfil === 'SUPER_ADMIN';
  const gerente = perfil === 'GERENTE_EMPRESA';

  const [form, setForm] = useState({ tipo: 'SUGESTAO' as TipoFeedback, titulo: '', mensagem: '', nota: '' });
  const [meus, setMeus] = useState<Feedback[]>([]);
  const [tenant, setTenant] = useState<Feedback[]>([]);
  const [global, setGlobal] = useState<Feedback[]>([]);
  const [filtros, setFiltros] = useState({ tenant_id: '', tipo: '', status: '' });
  const [drafts, setDrafts] = useState<Record<string, { status: StatusFeedback; resposta_admin: string }>>({});
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [salvandoId, setSalvandoId] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [paginacao, setPaginacao] = useState<MetadadosPaginacao>(paginacaoVazia);

  const carregar = useCallback(async (pagina = 1) => {
    setCarregando(true);
    setErro(null);
    try {
      if (superAdmin) {
        const resposta = await api.get('/admin/feedback', {
          params: {
            tenant_id: filtros.tenant_id || undefined,
            tipo: filtros.tipo || undefined,
            status: filtros.status || undefined,
            pagina,
            limite: paginacao.limite
          }
        });
        const itens = resposta.data?.itens || [];
        setGlobal(itens);
        setPaginacao(resposta.data?.paginacao || paginacaoVazia);
        setDrafts(Object.fromEntries(itens.map((item: Feedback) => [
          item.id,
          { status: item.status, resposta_admin: item.resposta_admin || '' }
        ])));
      } else {
        const chamadas = [api.get('/feedback/meus', { params: { pagina, limite: paginacao.limite } })];
        if (gerente) chamadas.push(api.get('/feedback/tenant', { params: { pagina, limite: paginacao.limite } }));
        const [meusResp, tenantResp] = await Promise.all(chamadas);
        setMeus(meusResp.data?.itens || []);
        setTenant(tenantResp?.data?.itens || []);
        setPaginacao(meusResp.data?.paginacao || paginacaoVazia);
      }
    } catch (erroReq: any) {
      setErro(extrairMensagemErro(erroReq, 'Erro ao carregar feedbacks.'));
    } finally {
      setCarregando(false);
    }
  }, [filtros.status, filtros.tenant_id, filtros.tipo, gerente, paginacao.limite, superAdmin]);

  useEffect(() => {
    carregar(1);
  }, [carregar]);

  const enviarFeedback = async (evento: React.FormEvent) => {
    evento.preventDefault();
    setErro(null);
    setMensagem(null);
    setEnviando(true);
    try {
      await api.post('/feedback', {
        tipo: form.tipo,
        titulo: form.titulo,
        mensagem: form.mensagem,
        nota: form.nota ? Number(form.nota) : undefined
      });
      setMensagem('Feedback enviado com sucesso.');
      setForm({ tipo: 'SUGESTAO', titulo: '', mensagem: '', nota: '' });
      await carregar();
    } catch (erroReq: any) {
      setErro(extrairMensagemErro(erroReq, 'Erro ao enviar feedback.'));
    } finally {
      setEnviando(false);
    }
  };

  const alterarDraft = (id: string, campo: 'status' | 'resposta_admin', valor: string) => {
    setDrafts((atuais) => ({
      ...atuais,
      [id]: {
        status: campo === 'status' ? valor as StatusFeedback : atuais[id]?.status || 'NOVO',
        resposta_admin: campo === 'resposta_admin' ? valor : atuais[id]?.resposta_admin || ''
      }
    }));
  };

  const salvarAdmin = async (feedback: Feedback) => {
    const draft = drafts[feedback.id];
    if (!draft) return;

    setErro(null);
    setMensagem(null);
    setSalvandoId(feedback.id);
    try {
      await api.patch(`/admin/feedback/${feedback.id}`, {
        status: draft.status,
        resposta_admin: draft.resposta_admin
      });
      setMensagem('Feedback atualizado com sucesso.');
      await carregar();
    } catch (erroReq: any) {
      setErro(extrairMensagemErro(erroReq, 'Erro ao atualizar feedback.'));
    } finally {
      setSalvandoId(null);
    }
  };

  return (
    <div className="workspace-page feedback-page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Feedback</span>
          <h1>Feedback da plataforma</h1>
          <p>Registe bugs percebidos, sugestões, desempenho, usabilidade e experiência geral de uso.</p>
        </div>
        <button className="botao botao-secundario" type="button" onClick={() => carregar(paginacao.pagina)} disabled={carregando}>
          <RefreshCw size={16} />
          Atualizar
        </button>
      </header>

      {erro && <div className="alerta alerta-erro">{erro}</div>}
      {mensagem && <div className="alerta alerta-sucesso">{mensagem}</div>}
      {carregando && <div className="inline-loading"><div className="spinner"></div><span>A carregar feedbacks...</span></div>}

      {superAdmin ? (
        <section className="panel">
          <div className="panel-heading">
            <h2>Visão global</h2>
            <span className="status-pill">{global.length}</span>
          </div>
          <div className="admin-filters feedback-filters">
            <input
              value={filtros.tenant_id}
              onChange={(evento) => setFiltros((atuais) => ({ ...atuais, tenant_id: evento.target.value }))}
              placeholder="Filtrar por tenant_id"
            />
            <select value={filtros.tipo} onChange={(evento) => setFiltros((atuais) => ({ ...atuais, tipo: evento.target.value }))}>
              <option value="">Todos os tipos</option>
              {tipos.map((tipo) => <option key={tipo} value={tipo}>{tipo}</option>)}
            </select>
            <select value={filtros.status} onChange={(evento) => setFiltros((atuais) => ({ ...atuais, status: evento.target.value }))}>
              <option value="">Todos os status</option>
              {statuses.map((status) => <option key={status} value={status}>{status}</option>)}
            </select>
          </div>
          <FeedbackTabela
            feedbacks={global}
            vazio="Sem feedbacks globais para mostrar."
            admin
            drafts={drafts}
            salvandoId={salvandoId}
            onDraftChange={alterarDraft}
            onSalvar={salvarAdmin}
          />
        </section>
      ) : (
        <div className="feedback-layout">
          <section className="panel">
            <div className="panel-heading">
              <h2>Novo feedback</h2>
              <span className="status-pill status-warning">Interno</span>
            </div>
            <form className="feedback-form" onSubmit={enviarFeedback}>
              <label>
                Tipo
                <select value={form.tipo} onChange={(evento) => setForm((atual) => ({ ...atual, tipo: evento.target.value as TipoFeedback }))}>
                  {tipos.map((tipo) => <option key={tipo} value={tipo}>{tipo}</option>)}
                </select>
              </label>
              <label>
                Título
                <input
                  value={form.titulo}
                  maxLength={120}
                  onChange={(evento) => setForm((atual) => ({ ...atual, titulo: evento.target.value }))}
                  required
                />
              </label>
              <label>
                Mensagem
                <textarea
                  value={form.mensagem}
                  maxLength={4000}
                  onChange={(evento) => setForm((atual) => ({ ...atual, mensagem: evento.target.value }))}
                  required
                />
              </label>
              <label>
                Nota opcional
                <select value={form.nota} onChange={(evento) => setForm((atual) => ({ ...atual, nota: evento.target.value }))}>
                  <option value="">Sem nota</option>
                  {[1, 2, 3, 4, 5].map((nota) => <option key={nota} value={nota}>{nota}</option>)}
                </select>
              </label>
              <button className="botao botao-primario" type="submit" disabled={enviando}>
                <Send size={16} />
                Enviar
              </button>
            </form>
          </section>

          <section className="panel">
            <div className="panel-heading">
              <h2>Meus feedbacks</h2>
              <span className="status-pill">{meus.length}</span>
            </div>
            <FeedbackTabela feedbacks={meus} vazio="Ainda não enviou feedbacks." />
          </section>

          {gerente && (
            <section className="panel feedback-tenant-panel">
              <div className="panel-heading">
                <h2>Feedbacks do tenant</h2>
                <span className="status-pill">{tenant.length}</span>
              </div>
              <FeedbackTabela feedbacks={tenant} vazio="Sem feedbacks do tenant para mostrar." />
            </section>
          )}
        </div>
      )}
      {!carregando && <Paginacao paginacao={paginacao} carregando={carregando} aoMudarPagina={carregar} />}
    </div>
  );
};

export default FeedbackPlataforma;
